import {ChangeDetectionStrategy, Component, computed, effect, inject, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {map} from 'rxjs';
import {Project} from 'src/app/core/model/project';
import {UpdateCurrentProjectsRouteAction, UpdateProjectViewTaskId} from 'src/app/core/store/projects/projects.action';
import {selectProjectsMap} from 'src/app/core/store/projects/projects.selector';
import {GetAllTrackersAction} from 'src/app/core/store/trackers/trackers.action';
import {ProjectsChallengeComponent} from '../challenge/projects-challenge.component';
import {ProjectEditComponent} from '../edit/project-edit.component';
import {ProjectViewComponent} from '../view/project-view.component';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {ProjectsHeaderComponent} from '../header/projects-header.component';

export type WrapperView = 'loading' | 'challenge' | 'project' | 'edit';

@Component({
  selector: 'project-challenge-wrapper',
  standalone: true,
  imports: [
    OphLoadingModule,
    ProjectsChallengeComponent,
    ProjectEditComponent,
    ProjectViewComponent,
    ProjectsHeaderComponent,
  ],
  templateUrl: './project-challenge-wrapper.component.html',
  styleUrl: './project-challenge-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectChallengeWrapperComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  readonly projectId = toSignal(this.route.paramMap.pipe(map(p => p.get('projectId') ?? null)), {
    initialValue: null as string | null,
  });
  readonly projectsMap = toSignal(this.store.select(selectProjectsMap), {
    initialValue: {} as Record<string, Project>,
  });

  readonly project = computed(() => {
    const id = this.projectId();
    const map = this.projectsMap();
    return id && map?.[id] ? map[id] : null;
  });

  /** When true, show project edit view instead of project view. Toggled by header Edit button. */
  readonly showEditView = signal(false);

  readonly view = computed<WrapperView>(() => {
    if (this.showEditView()) return 'edit';
    const p = this.project();
    if (!p) return 'loading';
    return p.isChallenge ? 'challenge' : 'project';
  });

  /** The project ID that was successfully loaded, used to detect deletion. */
  private loadedProjectId: string | null = null;

  constructor() {
    effect(
      () => {
        const p = this.project();
        const id = this.projectId();
        const projectsMap = this.projectsMap();
        const mapHasEntries = Object.keys(projectsMap).length > 0;

        if (p?._id) {
          this.loadedProjectId = p._id;
          this.store.dispatch(new UpdateProjectViewTaskId({id: null}));
          this.store.dispatch(new GetAllTrackersAction({params: {projectId: p._id, pageSize: 1000}}));
        } else if (this.loadedProjectId && this.loadedProjectId === id && mapHasEntries) {
          this.loadedProjectId = null;
          this.store.dispatch(new UpdateCurrentProjectsRouteAction({route: '/projects'}));
          sessionStorage.removeItem('ophStoredProjectId');
          this.router.navigate(['/projects']);
        }
      },
      {allowSignalWrites: true}
    );
  }

  onProjectSelected(project: Project) {
    this.showEditView.set(false);
    this.router.navigate(['/projects', project._id]);
  }

  onEditClick() {
    const p = this.project();
    if (!p?._id) return;
    if (p.isChallenge) {
      this.router.navigate(['/projects', 'challenge-builder', p._id, 'overview']);
    } else {
      this.router.navigate(['/projects', p._id, 'edit']);
    }
  }
}
